/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  useTheme,
  FormHelperText,
} from '@material-ui/core';
import { navigate } from 'gatsby';

import useContentMgmt from '../../../hooks/useContentMgmt';

import colors from '../../../themes/main-colors';
import routes from '../../../routes';

import { homeTypes } from './homeTypes';
import PopoverToolTip from '../../pge-plus-common/PopoverToolTip';
import RadioButtonGroup from '../../pge-plus-common/RadioButtonGroup';
import CommonButton from '../../pge-plus-common/CommonButton';
import { DropdownMenu } from '../../pge-plus-common/DropdownMenu';
import { ProceedOrCancelButton } from '../../pge-plus-common/StepperForm';

import PhoneTextField from '../../phone-text-field';
import TextField from '../../text-field';
import EmailTextField from '../../email-text-field';

import { ContactInfo as ContactInfoFormType } from '../evcharger.types';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import useFormState from '../../../hooks/useFormState';
import { createContactInfoValidateFunction } from '../../pge-plus-common/PGEPlusForm.rules';
import WelcomeToPGEPlusModal from '../../pge-plus-common/WelcomeToPGEPlusModal';
import {
  ApplicationEvSectionType,
  HomeType,
  IneligibilityReasonCode,
  SectionEvAccountInfo,
} from '../../../__generated__/pgeplus-types';
import useUpdateEVSections from '../../../hooks/pge-plus/useUpdateEVSections';
import { contactInfoFormStateAdapter } from '../../pge-plus-common/utils/pgeplusFormstateAdapters';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { ContactInfoBox } from './ContactInfoBox';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useUtil from '../../../hooks/pge-plus/useUtil';

interface ContactInfoProps {
  children?: React.ReactNode;
  path: string;
}

export const ContactInfo: React.FC<ContactInfoProps> = () => {
  const {
    formState,
    formState: { contactInfo, selectCharger, application },
    updateFormState,
    getSectionData,
  } = useEVChargerFormState();

  const sectionDataAccountInfo: SectionEvAccountInfo = getSectionData(
    ApplicationEvSectionType.EvAccountInfo,
  );
  const { updateSectionContactInfo } = useUpdateEVSections();

  const { content } = useContentMgmt(microcopyGroupIds.PGE_PLUS_CONTACT_INFO);
  const form = useFormState(contactInfo, {
    validate: createContactInfoValidateFunction(),
    validationContext: {
      content,
    },
  });
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();
  const theme = useTheme();

  const onSubmit = wrapWithLoader(async (data: ContactInfoFormType) => {
    try {
      const response = await updateSectionContactInfo(
        contactInfoFormStateAdapter().fromFormState(
          data,
          application?.id!,
          sectionDataAccountInfo?.id,
        ),
      );

      if (response.data?.updateSectionEVAccountInfo.validation?.success) {
        //Store the data in global store
        updateFormState('contactInfo', data);

        if (
          data.isOwnHome === false &&
          (data.homeType === HomeType.Apartment ||
            data.homeType === HomeType.Condo ||
            data.homeType === HomeType.Triplex)
        ) {
          void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
            state: {
              errorType:
                IneligibilityReasonCode.IneligibleOwnerType +
                IneligibilityReasonCode.IneligibleHomeType,
            },
          });
          return false;
        } else if (data.isOwnHome === false) {
          void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
            state: { errorType: IneligibilityReasonCode.IneligibleOwnerType },
          });
          return false;
        } else if (
          data.homeType === HomeType.Apartment ||
          data.homeType === HomeType.Condo ||
          data.homeType === HomeType.Triplex
        ) {
          void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
            state: { errorType: IneligibilityReasonCode.IneligibleHomeType },
          });
          return false;
        }

        //Navigate to the next screen
        void navigate(routes.PGE_PLUS_EV_CHARGERS_REBATE_ELIGIBILITY);
      } else {
        //TODO: Handle the validation error messages
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const onPhoneNumberUpdate = (phone: string) => {
    updateFormState('application', {
      ...application,
      //@ts-ignore
      details: application?.details.map(details => {
        if (details.sectionType === ApplicationEvSectionType.EvAccountInfo) {
          const accountInfo = details as SectionEvAccountInfo;
          if (accountInfo.data.accountContact) {
            return {
              ...details,
              data: {
                ...accountInfo.data,
                accountContact: {
                  ...accountInfo.data.accountContact,
                  phoneNumber: phone,
                },
              },
            };
          } else {
            return details;
          }
        }
        return details;
      }),
    });
  };

  const relationshipOptions = [
    { label: content.get('RELATIONSHIP_PARENT'), value: 'parent' },
    { label: content.get('RELATIONSHIP_SIBLING'), value: 'sibling' },
    { label: content.get('RELATIONSHIP_SPOUSE'), value: 'spouse' },
    { label: content.get('RELATIONSHIP_PARTNER'), value: 'partner' },
    { label: content.get('RELATIONSHIP_FRIEND'), value: 'friend' },
    { label: content.get('RELATIONSHIP_OTHER'), value: 'other' },
  ];

  // Styles
  const subText1 = {
    fontSize: 20,
    marginBottom: theme.spacing(4),
  };
  const boldText = {
    fontWeight: 700,
  };

  const divider = {
    color: colors.lightGray1,
  };

  const subTitleHolder = {
    fontWeight: 700,
    fontFamily: 'Untitled-Sans',
    margin: theme.spacing(0, 0, 3, 0),
    [`@media (min-width: ${theme.breakpoints.values.sm}px)`]: {
      fontSize: '20px',
    },
  };

  const mainContactFields = {
    width: '100%',
    margin: '8px 0 8px 0',
  };

  const userInfo = {
    firstName: sectionDataAccountInfo?.data?.accountContact?.firstName,
    middleName: sectionDataAccountInfo?.data?.accountContact?.middleName,
    lastName: sectionDataAccountInfo?.data?.accountContact?.lastName,
    address: sectionDataAccountInfo?.data?.address?.addressLine1 || '',
    phoneNumber:
      sectionDataAccountInfo?.data?.accountContact?.phoneNumber || '',
    emailAddress: sectionDataAccountInfo?.data?.accountContact?.emailAddress,
  };

  return (
    <Fragment>
      <form
        onSubmit={form.submit(values => onSubmit(values), console.log)}
        noValidate
      >
        {/* Generic Description */}
        <Box>
          <Typography css={{ ...subText1 }}>
            {content.get('WELCOME_TO_PGE_PLUS')}
          </Typography>
          <Typography css={{ ...subText1 }}>
            {content.get('CONTACT_INFO_DESCRIPTION')}
          </Typography>
          <Typography css={{ ...subText1, ...boldText }}>
            {content.get('CONFIRM_YOUR_CONTACT_INFO')}
          </Typography>
        </Box>

        {/* Contact Info Section */}
        <ContactInfoBox
          content={content}
          userInfo={userInfo}
          onPhoneNumberUpdate={onPhoneNumberUpdate}
        />

        {/* Main Contact Section */}
        <Box
          css={{ marginTop: theme.spacing(3) }}
          data-testid="main-contact-section"
        >
          <Typography
            data-testid="mailing-address-title"
            css={{ ...subTitleHolder }}
            variant="h4"
          >
            {content.get('MAIN_POINT_OF_CONTACT')}
          </Typography>
          <Box display={'flex'} justifyContent={'center'}>
            <Grid xs={12} md={6}>
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: true,
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: false,
                  },
                ]}
                state={form.values.isMainPerson}
                setState={e => form.setValue('isMainPerson', e)}
              />
            </Grid>
          </Box>
          {form.errors.isMainPerson && (
            <FormHelperText error>{form.errors.isMainPerson}</FormHelperText>
          )}
        </Box>

        {/* Main contact fields */}
        {form.values.isMainPerson === false && (
          <Box
            mt={2}
            css={{
              '& .MuiFormControl-root': {
                background: 'transparent',
              },
              '& .MuiInputBase-input': {
                background: '#FFF',
              },
            }}
          >
            <Typography
              variant="body2"
              css={{ marginTop: 30, marginBottom: 20, fontSize: '20px' }}
            >
              {content.get('ENTER_POINT_OF_CONTACT_DETAILS')}
            </Typography>

            <Grid container direction="column">
              <Grid container item spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    css={{ ...mainContactFields }}
                    name={'name'}
                    label={content.get('FIRST_NAME')}
                    {...form.props('firstName')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    css={{ ...mainContactFields }}
                    name={'name'}
                    label={content.get('LAST_NAME')}
                    {...form.props('lastName')}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container item spacing={2}>
              <Grid item xs={12} md={6}>
                <PhoneTextField
                  data-testid="primaryPhone"
                  label={content.get('REUSABLE_PHONE')}
                  name="phoneNumber"
                  {...form.props('phoneNumber')}
                  error={Boolean(form.errors.phoneNumber)}
                  helperText={form.errors.phoneNumber}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EmailTextField
                  css={{ ...mainContactFields }}
                  label={content.get('REUSABLE_EMAIL')}
                  name="email"
                  inputProps={{ minLength: 1, maxLength: 60 }}
                  {...form.props('email')}
                />
              </Grid>
            </Grid>

            <Typography
              variant="body2"
              css={{
                fontSize: '14px',
                lineHeight: '20px',
                marginTop: 5,
                marginBottom: 20,
              }}
            >
              {content.get('REUSABLE_PHONE_DISCLAIMER')}
            </Typography>

            <Box mt={2}>
              <DropdownMenu
                name={content.get('RELATIONSHIP_TO_USER')}
                options={relationshipOptions}
                state={form.values.userRelationship || ''}
                setState={e => {
                  void form.setValue('userRelationship', e);
                }}
                errorMsg={(form.errors.userRelationship as string) || ''}
              />
            </Box>
            <Divider css={{ ...divider, margin: theme.spacing(4, 0) }} />
          </Box>
        )}

        {/* Do you own home section */}
        <Box
          css={{ marginTop: theme.spacing(3) }}
          data-testid="main-contact-section"
        >
          <Box display={'flex'}>
            <Typography
              data-testid="mailing-address-title"
              css={{ ...subTitleHolder }}
              variant="h4"
            >
              {content.get('DO_YOU_OWN_THE_HOUSE')}
              <PopoverToolTip
                popoverContent={content.get('DO_YOU_OWN_THE_HOUSE_TOOLTIP')}
              />
            </Typography>
          </Box>
          <Box display={'flex'} justifyContent={'center'}>
            <Grid xs={12} md={6}>
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: true,
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: false,
                  },
                ]}
                state={form.values.isOwnHome}
                setState={e => form.setValue('isOwnHome', e)}
              />
            </Grid>
          </Box>

          {form.errors.isOwnHome && (
            <FormHelperText error>{form.errors.isOwnHome}</FormHelperText>
          )}
        </Box>

        {form.values.isOwnHome !== undefined && (
          <Fragment>
            <Box display={'flex'} css={{ marginTop: theme.spacing(3) }}>
              <Typography
                data-testid="mailing-address-title"
                css={{ ...subTitleHolder }}
                variant="h4"
              >
                {content.get('WHAT_KIND_OF_STRUCTURE')}
                <PopoverToolTip
                  popoverContent={content.get('WHAT_KIND_OF_STRUCTURE_TOOLTIP')}
                />
              </Typography>
            </Box>

            {/* Type of property buttons section */}
            <Grid
              container
              spacing={2}
              css={{ justifyContent: 'space-between' }}
            >
              {homeTypes(content).map(home => (
                <Grid item xs={6} md={4}>
                  <CommonButton
                    buttonLabel={home.textLabel}
                    handleOnClick={(e: React.MouseEvent<HTMLElement>) =>
                      form.setValue('homeType', home.formValue)
                    }
                    isActive={form.values.homeType === home.formValue}
                    img={home.img}
                  />
                </Grid>
              ))}
            </Grid>

            {form.errors.homeType && (
              <FormHelperText error>{form.errors.homeType}</FormHelperText>
            )}

            <Divider css={{ ...divider, margin: theme.spacing(3, 0) }} />
          </Fragment>
        )}

        <ProceedOrCancelButton
          back={
            selectCharger.suppliedByCustomer
              ? routes.PGE_PLUS_EV_CHARGERS_CONFIRM_YOUR_CHARGER
              : false
          }
          disable={
            form.values.isOwnHome === undefined || !form.values.homeType
              ? true
              : false
          }
          isCancel={!selectCharger.suppliedByCustomer}
          backLabel={
            selectCharger.suppliedByCustomer
              ? content.get('REUSABLE_BUTTON_BACK')
              : content.get('CANCEL')
          }
        />
      </form>

      {!selectCharger.suppliedByCustomer && <WelcomeToPGEPlusModal />}
    </Fragment>
  );
};
